'use client';

import React from 'react';
import { Control } from 'react-hook-form';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DatePicker,
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { SelectGroup, SelectItem } from '@/components/ui/select';

interface RankingsAdmissionsStepProps {
  control: Control<any>;
  isEdit: boolean;
  errors?: any;
  selectedCategoryName?: string;
}

export default function RankingsAdmissionsStep({
  control,
  selectedCategoryName,
}: RankingsAdmissionsStepProps) {
  const showTopSecondaries =
    selectedCategoryName !== 'dayschool' && selectedCategoryName !== 'daycare';
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-2 gap-4 items-end">
        <FormField
          control={control}
          name="setting"
          render={({ field }) => {
            return (
              <FormItem className="flex-[2]">
                <FormLabel className="xl:text-sm ml-1">Setting</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => field.onChange(value)}
                >
                  <FormControl>
                    <SelectTrigger className=" data-[size=default]:h-9 w-full xl:text-sm rounded-full border-[#cdc4c1] text-black h-10  data-[placeholder]:text-muted-foreground  data-[placeholder]:text-sm">
                      <SelectValue placeholder="Select setting" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      {['Urban', 'Rural'].map((option) => (
                        <SelectItem key={option} value={option}>
                          <div className="flex flex-col w-full items-start xl:text-lg text-sm">
                            <span className="font-medium">{option}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={control}
          name="enrollment"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="xl:text-sm ml-1">
                Total Students Enrolled
              </FormLabel>

              <FormControl>
                <Input
                  {...field}
                  className="placeholder:text-muted-foreground placeholder:text-sm"
                  placeholder="Enter number of students"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 items-end">
        <FormField
          control={control}
          name="acceptanceRate"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="xl:text-sm ml-1">
                Acceptance Rate (%)
              </FormLabel>

              <FormControl>
                <Input
                  {...field}
                  className="placeholder:text-muted-foreground placeholder:text-sm"
                  placeholder="80"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="applicationFee"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="xl:text-sm ml-1">
                Application Fee ($)
              </FormLabel>

              <FormControl>
                <Input
                  {...field}
                  className="placeholder:text-muted-foreground placeholder:text-sm"
                  placeholder="200"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* <div className="grid grid-cols-2 gap-4"> */}
      <FormField
        control={control}
        name="studentFacultyRatio"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="xl:text-sm ml-1">
              Student-Faculty Ratio
            </FormLabel>

            <FormControl>
              <Input
                {...field}
                className="placeholder:text-muted-foreground placeholder:text-sm"
                placeholder="15:1"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="applicationDeadline"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="xl:text-sm  ml-1">
              Application Deadline
            </FormLabel>
            <FormControl>
              <div className="relative">
                <DatePicker
                  placeholder="Select deadline"
                  className="w-full rounded-full justify-start text-left font-normal h-12 border-[#cdc4c1] text-black xl:text-lg text-sm "
                  value={field.value}
                  onChange={field.onChange}
                  disabledBeforeToday
                />
                {field.value && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-gray-200"
                    onClick={() => field.onChange(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* </div> */}

      <FormField
        control={control}
        name="method"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="xl:text-sm ml-1">Teaching Method</FormLabel>

            <FormControl>
              <Input
                className="border-1 placeholder:text-muted-foreground placeholder:text-sm"
                {...field}
                placeholder="Montessori, IB, Traditional"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {showTopSecondaries && (
        <FormField
          control={control}
          name="topSecondaries"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="xl:text-sm ml-1">
                Top Secondary Schools
              </FormLabel>

              <FormControl>
                <Textarea
                  {...field}
                  placeholder="List the secondary schools students graduate to (separated by commas)"
                  rows={2}
                  className="max-w-full min-h-[80px] resize-none placeholder:text-muted-foreground placeholder:text-sm"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
